import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from '@/auth/auth-provider';
import { AppLayout } from '@/components/layout/app-layout';
import { TableSkeleton } from '@/components/fleet/states';
import { BootstrapPoolPage } from '@/pages/bootstrap-pool';
import { FleetDashboardPage } from '@/pages/fleet-dashboard';
import { FleetDetailPage } from '@/pages/fleet-detail';
import { FleetListPage } from '@/pages/fleet-list';
import { FleetPlaybooksPage } from '@/pages/fleet-playbooks';
import { FleetRegionsPage } from '@/pages/fleet-regions';
import { RegionsAnalyticsPage } from '@/pages/insights-regions';
import { UserGeographyPage } from '@/pages/insights-users';
import { LoginPage } from '@/pages/login';
import { OverviewPage } from '@/pages/overview';
import { SecurityPage } from '@/pages/security';
import { SupportBugReportDetailPage } from '@/pages/support-bug-report-detail';
import { SupportBugReportsPage } from '@/pages/support-bug-reports';
import { StoreMetadataPage } from '@/pages/store-metadata';

// react-flow is a heavy dependency — code-split the topology page so it only
// loads when an operator actually navigates to the graph.
const FleetTopologyPage = lazy(() =>
  import('@/pages/fleet-topology').then((m) => ({ default: m.FleetTopologyPage })),
);

function CenteredMessage({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center">
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

/** Gate the Fleet section: admin + support may view; other roles may not. */
function RequireFleetAccess({ children }: { children: JSX.Element }): JSX.Element {
  const { user, canViewFleet } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!canViewFleet) {
    return (
      <CenteredMessage
        title="Not authorized"
        body="The Fleet section requires the admin or support role."
      />
    );
  }

  return children;
}

/** Gate an admin-only section — support (who passes RequireFleetAccess) is refused. */
function RequireAdmin({ children }: { children: JSX.Element }): JSX.Element {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <CenteredMessage
        title="Not authorized"
        body="Store Metadata is restricted to the admin role."
      />
    );
  }

  return children;
}

export default function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/overview" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireFleetAccess>
            <AppLayout />
          </RequireFleetAccess>
        }
      >
        {/* Insights */}
        <Route path="/overview" element={<OverviewPage />} />
        <Route path="/insights/regions" element={<RegionsAnalyticsPage />} />
        <Route path="/insights/users" element={<UserGeographyPage />} />

        {/* Operations */}
        <Route path="/fleet" element={<FleetListPage />} />
        {/* Static segments must precede `:name` so they are not captured as a server. */}
        <Route
          path="/fleet/topology"
          element={
            <Suspense fallback={<TableSkeleton rows={6} />}>
              <FleetTopologyPage />
            </Suspense>
          }
        />
        <Route path="/fleet/dashboard" element={<FleetDashboardPage />} />
        <Route path="/fleet/regions" element={<FleetRegionsPage />} />
        <Route path="/fleet/playbooks" element={<FleetPlaybooksPage />} />
        <Route path="/fleet/:name" element={<FleetDetailPage />} />
        {/* `/fleet/playbooks` is above `:name` so it isn't captured as a server. */}

        {/* Store — admin only */}
        <Route
          path="/store"
          element={
            <RequireAdmin>
              <StoreMetadataPage />
            </RequireAdmin>
          }
        />

        {/* Bootstrap-edge pool editor — admin only */}
        <Route
          path="/bootstrap-pool"
          element={
            <RequireAdmin>
              <BootstrapPoolPage />
            </RequireAdmin>
          }
        />

        {/* Bug-bounty confirm queue (PRD-0007) — admin only: confirming pays out */}
        <Route
          path="/support/bug-reports"
          element={
            <RequireAdmin>
              <SupportBugReportsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/support/bug-reports/:reference"
          element={
            <RequireAdmin>
              <SupportBugReportDetailPage />
            </RequireAdmin>
          }
        />

        {/* Security controls (config-signing toggle) — admin only */}
        <Route
          path="/security"
          element={
            <RequireAdmin>
              <SecurityPage />
            </RequireAdmin>
          }
        />
      </Route>
      <Route
        path="*"
        element={<CenteredMessage title="Page not found" body="That route does not exist." />}
      />
    </Routes>
  );
}
